import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { Menu } from 'primereact/menu';
import ImpersonateDialog from '../copy/dialogs/ImpersonateDialog';
import HeaderUserImpersonateNotice from './HeaderUserImpersonateNotice';
import { UserModel } from 'services/EpdClient';
import HeaderUserInfo from './HeaderUserInfo';
import { CompanyContext } from 'contexts/CompanyContextProvider';
import { EpdLinks } from 'routes/EpdRoutes';
import AuthService from 'services/AuthService';
import HeaderSquareButton from './HeaderSquareButton';
import FontIcon from 'components/icons/FontIcon';
import useHeaderAccessToCompanySettings from './useHeaderAccessToCompanySettings';
import HeaderUserCompanyInfo from './HeaderUserCompanyInfo';
import { cutstomHeaderMenuChangedEmitter, setCustomHeaderMenuEmitter } from './components/CustomHeaderMenu';
import { media } from './components/headerHelpers';
import useCurrentUser from 'hooks/useCurrentUser';

type UserMenuItem = {
  visible?: boolean;
  template?: React.ReactNode;
  separator?: boolean;
  label?: string;
  url?: string;
  command?(): void;
  icon?: React.ReactNode;
  greenText?: boolean;
};

interface Props {
  isImpersonated: boolean;
}

const HeaderUser: React.FC<Props> = ({ isImpersonated }) => {
  const [isOpenedImpersonateDialog, setIsOpenedImpersonateDialog] = useState<boolean>(false);
  const [currentHeaderMenu, setCurrentHeaderMenu] = useState<string | null>(null);

  const buttonWrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const removeCustomHeaderListener = cutstomHeaderMenuChangedEmitter.addListener(setCurrentHeaderMenu);
    return removeCustomHeaderListener;
  }, []);

  const handleToggleMenu = () => {
    if (currentHeaderMenu === 'user_menu') {
      setCustomHeaderMenuEmitter.emitEvent(null);
      return;
    }
    setCustomHeaderMenuEmitter.emitEvent({
      id: 'user_menu',
      parent: buttonWrapperRef.current as HTMLElement,
      element: (
        <CustomMenuContent
          isImpersonated={isImpersonated}
          onOpenImpersonateDialog={() => setIsOpenedImpersonateDialog(true)}
        />
      ),
    });
  };

  return (
    <>
      <div ref={buttonWrapperRef}>
        <HeaderSquareButton
          icon={isImpersonated ? <FontIcon icon="impersonate" /> : 'pi pi-user'}
          onClick={handleToggleMenu}
          $isOrange={isImpersonated}
          $isHighlighted={currentHeaderMenu === 'user_menu'}
        />
      </div>
      <ImpersonateDialog visible={isOpenedImpersonateDialog} onHide={() => setIsOpenedImpersonateDialog(false)} />
    </>
  );
};

export default HeaderUser;

const CustomMenuContent: React.FC<{ isImpersonated: boolean; onOpenImpersonateDialog(): void }> = ({
  isImpersonated,
  onOpenImpersonateDialog,
}) => {
  const companyAccess = useHeaderAccessToCompanySettings();

  const { t } = useTranslation();
  const history = useHistory();
  const { company } = useContext(CompanyContext);
  const isAdmin = AuthService.isAdmin();
  const user = useCurrentUser();

  const handleDismissPopupOnClick: React.MouseEventHandler<HTMLUListElement> = (ev) => {
    const target = ev.target as HTMLElement;
    const linkOrButton = target.closest('a') || target.closest('button');
    if (!linkOrButton) {
      return;
    }

    if (linkOrButton.hasAttribute('disabled')) {
      return;
    }

    setCustomHeaderMenuEmitter.emitEvent(null);
  };

  const itemsProfile: UserMenuItem[] = [
    {
      template: <HeaderUserImpersonateNotice user={user} />,
      visible: isImpersonated,
    },
    {
      template: <HeaderUserInfo user={user} />,
    },
    {
      separator: true,
    },
    {
      template: !!company?.id && (
        <HeaderUserCompanyInfo
          company={company}
          memberships={user.memberships}
          hasCompanyAccess={companyAccess.status === 'ready' && companyAccess.hasAccess}
        />
      ),
      visible: !!company?.id,
    },
    {
      label: 'User settings',
      url: EpdLinks.userSettings(),
    },
    {
      separator: isAdmin,
      visible: isAdmin,
    },
    {
      label: 'Impersonate',
      icon: <ShiftedFontIcon icon="users" className="p-menuitem-icon" />,
      command: onOpenImpersonateDialog,
      visible: isAdmin,
      greenText: true,
    },
    {
      separator: true,
    },
    {
      label: t('logout.title'),
      icon: <ShiftedFontIcon icon="log-out" className="p-menuitem-icon" />,
      command: () => AuthService.logout(history),
    },
  ];

  return (
    <DrawerUserMenu onClick={handleDismissPopupOnClick}>
      {itemsProfile.map((itm, i) => {
        if (itm.visible === false) {
          return null;
        }

        if (itm.separator) {
          return <Separator key={i} />;
        }

        if (itm.template) {
          return <PaddedListItem key={i}>{itm.template}</PaddedListItem>;
        }

        return (
          <PaddedListItem key={i}>
            {itm.command && (
              <CustomMenuButton onClick={itm.command} $greenText={itm.greenText}>
                {itm.icon}
                {itm.label}
              </CustomMenuButton>
            )}
            {itm.url && (
              <CustomMenuButton as={Link} to={itm.url} $greenText={itm.greenText}>
                {itm.icon}
                {itm.label}
              </CustomMenuButton>
            )}
          </PaddedListItem>
        );
      })}
    </DrawerUserMenu>
  );
};

const ShiftedFontIcon = styled(FontIcon)`
  transform: translateY(2.5px);
`;

const DrawerUserMenu = styled.ul`
  font-size: 16px;
  margin: 0;
  padding: 0;
  list-style: none;

  @media ${media.LESS_THAN_TABLET} {
    padding-bottom: 12px;
  }
`;

const Separator = styled.li`
  border-top: 1px solid ${(props) => props.theme.colors.elementsInputBorder};
  margin: 8px 0;
  height: 0;
`;

const PaddedListItem = styled.li`
  @media ${media.LESS_THAN_TABLET} {
    padding: 0 8px;
  }
`;

const CustomMenuButton = styled.button<{ $greenText?: boolean }>`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  min-height: 42px;
  padding-top: 12px;
  padding-bottom: 12px;
  padding-left: 16px;
  padding-right: 16px;

  border: none;
  background-color: transparent;
  cursor: pointer;
  font-size: inherit;
  font-family: inherit;
  text-align: left;
  text-decoration: none;
  color: ${(props) => (!props.$greenText ? props.theme.colors.primaryBlack : props.theme.colors.primaryGreen)};

  &:hover {
    background-color: ${(props) => props.theme.colors.secondaryLightGreen30};
    color: ${(props) => props.theme.colors.primaryGreen};
  }

  @media ${media.LESS_THAN_TABLET} {
    min-height: 60px;
  }
`;
